import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth } from "../firebase"; // <-- Import auth
import Footer from "../components/Footer";
import "../styles/theme.css";
import "./Checkout.css";

const OneTimeCheckout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    selectedOneTimePlan = { name: "One-Time Website", basePrice: 399 },
    selectedAddOns = [],
  } = location.state || {};

  const [currentUser, setCurrentUser] = useState(null);
  const [includeHosting, setIncludeHosting] = useState(false);
  const [hostingDuration, setHostingDuration] = useState("");
  const [hostingPrice, setHostingPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(selectedOneTimePlan.basePrice);

  // ✅ Get logged-in Firebase user
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // ✅ Update total price when selections change
  useEffect(() => {
    let base = selectedOneTimePlan.basePrice;
    const addOnsTotal = selectedAddOns.reduce((sum, addon) => sum + addon.price, 0);
    let hostingCost = 0;

    if (includeHosting) {
      if (hostingDuration === "1") hostingCost = 100;
      else if (hostingDuration === "2") hostingCost = 180;
      else if (hostingDuration === "3") hostingCost = 250;
    }

    setHostingPrice(hostingCost);
    setTotalPrice(base + addOnsTotal + hostingCost);
  }, [includeHosting, hostingDuration, selectedAddOns, selectedOneTimePlan]);

  // ✅ Handle Payment Navigation
  const handlePayment = () => {
    if (!currentUser) {
      alert("Please log in to continue.");
      return;
    }

    const userDetails = {
      name: currentUser.displayName || "No Name",
      email: currentUser.email || "No Email",
    };

    const hostingOption = includeHosting && hostingDuration
      ? { years: hostingDuration, price: hostingPrice }
      : null;

    navigate("/payment", {
      state: {
        selectedPlan: selectedOneTimePlan?.name || "One-Time Website",
        selectedAddOns,
        totalPrice,
        billingCycle: null,
        includeHosting: hostingOption?.years ? true : false,
        hostingDuration: hostingOption?.years || "",
        isOneTime: true,
        userDetails, // ✅ Now coming from Firebase
      },
    });
  };

  return (
    <div className="checkout-container">
      <h1>{t("checkout.title")}</h1>

      <div className="checkout-summary">
        <h2>{t("checkout.selected_plan")}: {selectedOneTimePlan.name}</h2>

        {selectedAddOns.length > 0 && (
          <ul>
            {selectedAddOns.map((addon, index) => (
              <li key={index}>{addon.name} - ${addon.price}</li>
            ))}
          </ul>
        )}

        <div className="hosting-addon">
          <label>
            <input
              type="checkbox"
              checked={includeHosting}
              onChange={() => {
                setIncludeHosting(!includeHosting);
                if (!includeHosting) setHostingDuration("");
              }}
            />
            {t("checkout.include_hosting")}
          </label>

          {includeHosting && (
            <select
              value={hostingDuration}
              onChange={(e) => setHostingDuration(e.target.value)}
            >
              <option value="">{t("checkout.select_duration")}</option>
              <option value="1">1 Year - $100</option>
              <option value="2">2 Years - $180 (Save 10%)</option>
              <option value="3">3 Years - $250 (Save 17%)</option>
            </select>
          )}
        </div>

        <h3>{t("checkout.total")}: ${totalPrice}</h3>
      </div>

      <button className="checkout-btn" onClick={handlePayment}>
        {t("checkout.pay_now")}
      </button>

      <Footer />
    </div>
  );
};

export default OneTimeCheckout;